import { LegalEntityParamFilter } from '../../entities/legal-entities/legal-entity-param-filter';
import { ManagerUrlParamsFilter } from '../../entities/filters/manager-url-params-filter';
import { DateUrlParamsFilter } from '../../entities/filters/date-url-params-filter';
import { useOurCompany } from '../../hooks/portal-settings.hook';
import { CONTRACT_STATUSES, CONTRACT_STATUSES_KEYS, CONTRACT_TYPE_CLIENT, CONTRACT_TYPE_SUPPLIER } from '@sr/dto';

import { SimpleSelectUrlParamsFilter } from 'entities/filters/simple-select-url-params-filter';
import { useSearchParams } from 'react-router-dom';
import { createRangeSummaryFormatter, FilterWrapper, ValueFormatterContext } from '../../shared/ui/filter/filter-wrapper';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useLegalEntityLabel } from 'shared/hooks/use-entity-labels';
import { Stack } from '@mui/material';

const TYPE_OPTIONS = [
  { value: CONTRACT_TYPE_CLIENT, label: 'Клиентский' },
  { value: CONTRACT_TYPE_SUPPLIER, label: 'Поставщика' },
];

const STATUS_OPTIONS = CONTRACT_STATUSES_KEYS.map((status) => ({ value: status, label: CONTRACT_STATUSES[status] }));

const statusFormatter = ({ value }: ValueFormatterContext) => STATUS_OPTIONS.find((item) => String(item.value) === value)?.label ?? value;

export const ContractsFilter = () => {
  const ourCompanyId = useOurCompany();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const selectedType = typeParam ? Number(typeParam) : undefined;
  const resolvedType = selectedType ?? CONTRACT_TYPE_CLIENT;
  const isClient = resolvedType === CONTRACT_TYPE_CLIENT;
  const { items: managers } = useManagersDropdownList();
  const sellerLegalEntityId = searchParams.get('sellerLegalEntityId');
  const buyerLegalEntityId = searchParams.get('buyerLegalEntityId');
  const sellerLabel = useLegalEntityLabel(sellerLegalEntityId);
  const buyerLabel = useLegalEntityLabel(buyerLegalEntityId);

  const typeFormatter = ({ value }: ValueFormatterContext) => {
    const option = TYPE_OPTIONS.find((item) => String(item.value) === value);
    return option ? option.label : value;
  };

  const managerFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((m) => String(m.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const sellerFormatter = ({ value }: ValueFormatterContext) => {
    if (sellerLabel && sellerLegalEntityId === value) return sellerLabel;
    return value;
  };

  const buyerFormatter = ({ value }: ValueFormatterContext) => {
    if (buyerLabel && buyerLegalEntityId === value) return buyerLabel;
    return value;
  };

  return (
    <FilterWrapper>
      <FilterWrapper.Item label="Тип" queryKeys="type" valueFormatter={typeFormatter}>
        <SimpleSelectUrlParamsFilter queryParamName="type" label="Тип" defaultValue={CONTRACT_TYPE_CLIENT} items={TYPE_OPTIONS} />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Статус" queryKeys="status" valueFormatter={statusFormatter}>
        <SimpleSelectUrlParamsFilter queryParamName="status" label="Статус" items={STATUS_OPTIONS} />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Продавец" queryKeys="sellerLegalEntityId" valueFormatter={sellerFormatter}>
        <LegalEntityParamFilter
          queryParamName={'sellerLegalEntityId'}
          companyId={isClient ? ourCompanyId : undefined}
          companyTypeFilter={{ isSupplier: isClient ? undefined : true }}
          label="Продавец"
        />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Покупатель" queryKeys="buyerLegalEntityId" valueFormatter={buyerFormatter}>
        <LegalEntityParamFilter
          queryParamName={'buyerLegalEntityId'}
          companyId={isClient ? undefined : ourCompanyId}
          companyTypeFilter={{ isClient: isClient ? true : undefined }}
          label="Покупатель"
        />
      </FilterWrapper.Item>

      {isClient && (
        <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerFormatter}>
          <ManagerUrlParamsFilter queryParamName="managerId" withNonSelect={true} />
        </FilterWrapper.Item>
      )}

      <FilterWrapper.Item
        label="Дата окончания"
        queryKeys={['fromDate', 'toDate']}
        summaryFormatter={createRangeSummaryFormatter({ fromKey: 'fromDate', toKey: 'toDate', emptyLabel: 'Дата окончания', activeLabel: 'Окончание' })}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={'fromDate'} label="Дата окончания с" />
          <DateUrlParamsFilter queryParamName={'toDate'} label="Дата окончания до" />
        </Stack>
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Дата создания"
        queryKeys={['createdAtFrom', 'createdAtTo']}
        summaryFormatter={createRangeSummaryFormatter({ fromKey: 'createdAtFrom', toKey: 'createdAtTo', emptyLabel: 'Дата создания', activeLabel: 'Создание' })}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={'createdAtFrom'} label="Дата создания с" />
          <DateUrlParamsFilter queryParamName={'createdAtTo'} label="Дата создания по" />
        </Stack>
      </FilterWrapper.Item>
    </FilterWrapper>
  );
};
